'use client';

import { useEffect, useRef, useState } from 'react';

type AcreageCounterProps = {
  acres: number;
};

type Phase = 'idle' | 'animating' | 'done';

const ANIM_DURATION_MS = 1600;
const COUNT_UP_OFFSET = 25;

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function Digit({ value }: { value: string }) {
  const prevRef = useRef(value);
  const [outgoing, setOutgoing] = useState<string | null>(null);

  useEffect(() => {
    if (value === prevRef.current) return;
    const previous = prevRef.current;
    prevRef.current = value;
    setOutgoing(previous);
    const t = window.setTimeout(() => setOutgoing(null), 400);
    return () => window.clearTimeout(t);
  }, [value]);

  return (
    <span className="relative inline-flex h-16 w-11 md:h-24 md:w-16 items-center justify-center rounded-lg bg-[#0a1614]/55 font-serif text-4xl md:text-6xl font-medium text-white ring-1 ring-white/15 backdrop-blur-sm overflow-hidden before:pointer-events-none before:absolute before:inset-x-0 before:top-1/2 before:h-px before:bg-white/10 before:z-20">
      <span
        key={`in-${value}`}
        className="absolute inset-0 flex items-center justify-center animate-digit-in"
      >
        {value}
      </span>
      {outgoing !== null && (
        <span
          key={`out-${outgoing}`}
          className="absolute inset-0 flex items-center justify-center animate-digit-out"
        >
          {outgoing}
        </span>
      )}
    </span>
  );
}

export default function AcreageCounter({ acres }: AcreageCounterProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [displayed, setDisplayed] = useState(acres);
  const [phase, setPhase] = useState<Phase>('idle');

  useEffect(() => {
    if (phase !== 'idle') return;
    const node = containerRef.current;
    if (!node) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();

        const start = Math.max(0, acres - COUNT_UP_OFFSET);
        setDisplayed(start);
        setPhase('animating');

        const startTime = performance.now();
        const tick = (now: number) => {
          const t = Math.min(1, (now - startTime) / ANIM_DURATION_MS);
          const value = Math.round(start + (acres - start) * easeOutCubic(t));
          setDisplayed(value);
          if (t < 1) {
            requestAnimationFrame(tick);
          } else {
            setPhase('done');
          }
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.3 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [acres, phase]);

  const chars = displayed.toLocaleString('en-US').split('');

  return (
    <div ref={containerRef} className="inline-flex items-center gap-1.5 align-middle">
      {chars.map((c, i) => {
        if (c === ',') {
          return (
            <span
              key={`comma-${i}`}
              className="font-serif text-4xl md:text-6xl font-medium text-white -mx-0.5"
              aria-hidden="true"
            >
              ,
            </span>
          );
        }
        const positionFromRight = chars.slice(i + 1).filter((char) => char !== ',').length;
        return <Digit key={`d-${phase === 'idle' ? 'pre' : 'live'}-${positionFromRight}`} value={c} />;
      })}
    </div>
  );
}
